export const INTELLIGENCE_REPORT_TYPE = 'IntelligenceAnalysisReport' as const;
export const INTELLIGENCE_LIMITS = { files: 3, fileBytes: 25 * 1024 * 1024, text: 60000, questions: 2000, context: 2000, runMs: 12 * 60 * 1000 };
export const INTELLIGENCE_FILE_TYPES: Record<string, string> = {
    pdf: 'application/pdf',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    ppsx: 'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
    txt: 'text/plain',
};
export type IntelligenceInput = { url: string; text: string; questions: string; context: string };
export type IntelligenceFile = { name: string; path: string; contentType: string; size: number };
export type IntelligenceSource = { id: string; title: string; url: string };
export type IntelligenceClaim = {
    claim: string; status: 'verified' | 'partial' | 'disputed' | 'unverified';
    rating: string; reason: string; sourceIds: string[];
};
export type IntelligenceAction = { priority: 'High' | 'Medium' | 'Low'; action: string; rationale: string; trigger: string };
export interface IntelligenceAnalysisReport {
    reportType: typeof INTELLIGENCE_REPORT_TYPE;
    title: string;
    organizationId: string;
    generatedAt: string;
    summary: string;
    credibility: string;
    corroboration: string;
    implications: string;
    impact: 'Low' | 'Moderate' | 'High' | 'Critical' | 'Unknown';
    claims: IntelligenceClaim[];
    actions: IntelligenceAction[];
    sources: IntelligenceSource[];
    dataGaps: string[];
    confidenceFactors: Record<'agreement' | 'quality' | 'timeliness' | 'specificity' | 'independence', number>;
    verificationScore: number;
    confidenceScore: number;
    scoreMethod: string;
    submittedSources: string[];
    questions: string;
    context: string;
}

function field(value: unknown, maximum: number, label: string, required = false): string {
    if (value !== undefined && typeof value !== 'string')
        throw new Error(label + ' must be text.');
    const result = (value as string | undefined)?.trim() || '';
    if (result.length > maximum || required && !result)
        throw new Error(label + (required && !result ? ' is required.' : ' is too long.'));
    return result;
}

export function intelligenceHttpsUrl(value: unknown): string {
    const url = new URL(field(value, 2048, 'URL', true));
    if (url.protocol !== 'https:' || url.username || url.password || url.port && url.port !== '443')
        throw new Error('Use a public HTTPS link without credentials or a custom port.');
    url.hash = '';
    return url.href;
}

export function intelligenceFileType(name: string): string {
    return INTELLIGENCE_FILE_TYPES[name.split('.').pop()?.toLowerCase() || ''] || '';
}

export function intelligenceFiles(value: unknown): Omit<IntelligenceFile, 'path'>[] {
    if (!Array.isArray(value) || value.length > INTELLIGENCE_LIMITS.files)
        throw new Error('Choose up to three documents.');
    return value.map(file => {
        const name = field(file?.name, 180, 'File name', true);
        const contentType = intelligenceFileType(name);
        if (!contentType || file.contentType !== contentType)
            throw new Error('Use PDF, DOCX, PPTX, PPSX, or TXT documents.');
        if (!Number.isSafeInteger(file.size) || file.size < 1 || file.size > INTELLIGENCE_LIMITS.fileBytes)
            throw new Error('Each document must contain data and be 25MB or smaller.');
        return { name, contentType, size: file.size as number };
    });
}

export function intelligenceInput(value: any, fileCount: number): IntelligenceInput {
    const result = {
        url: value?.url ? intelligenceHttpsUrl(value.url) : '',
        text: field(value?.text, INTELLIGENCE_LIMITS.text, 'Intelligence text'),
        questions: field(value?.questions, INTELLIGENCE_LIMITS.questions, 'Questions'),
        context: field(value?.context, INTELLIGENCE_LIMITS.context, 'Site and business context'),
    };
    if (!result.url && !result.text && !fileCount)
        throw new Error('Add a link, intelligence text, or a document.');
    return result;
}

export function intelligenceSources(chunks: any[]): IntelligenceSource[] {
    const sources: IntelligenceSource[] = [];
    const seen = new Set<string>();
    for (const chunk of chunks) {
        try {
            const url = intelligenceHttpsUrl(chunk?.web?.uri);
            if (seen.has(url))
                continue;
            seen.add(url);
            sources.push({ id: 'S' + (sources.length + 1), url, title: String(chunk.web.title || new URL(url).hostname).slice(0, 250) });
        } catch { /* Grounding can include non-web entries. */ }
        if (sources.length === 30)
            break;
    }
    return sources;
}

export function readIntelligenceReport(draft: any, sources: IntelligenceSource[], input: IntelligenceInput,
    organizationId: string, submittedSources: string[], intakeGaps: string[] = []): IntelligenceAnalysisReport {
    const text = (value: unknown, label: string, maximum = 5000) => field(value, maximum, label, true);
    if (!Array.isArray(draft?.claims) || !draft.claims.length || draft.claims.length > 12
        || !Array.isArray(draft.actions) || !draft.actions.length || draft.actions.length > 8
        || !Array.isArray(draft.dataGaps) || draft.dataGaps.length > 20)
        throw new Error('Provide one to twelve claims, one to eight actions, and a data gaps list.');
    const known = new Set(sources.map(source => source.id));
    let droppedReferences = false;
    const claims: IntelligenceClaim[] = draft.claims.map((claim: any) => {
        if (!['verified', 'partial', 'disputed', 'unverified'].includes(claim?.status)
            || !/^[A-F][1-6]$/.test(claim.rating) || !Array.isArray(claim.sourceIds))
            throw new Error('Every claim needs a valid status, Admiralty rating, and source IDs.');
        const sourceIds = [...new Set<string>(claim.sourceIds.filter((id: unknown) => typeof id === 'string' && known.has(id)))];
        droppedReferences ||= sourceIds.length !== claim.sourceIds.length;
        const supported = sourceIds.length > 0;
        return { claim: text(claim.claim, 'Claim', 1500), status: supported ? claim.status : 'unverified',
            rating: supported ? claim.rating : 'F6', reason: text(claim.reason, 'Claim reasoning', 2000), sourceIds };
    });
    const factors = {} as IntelligenceAnalysisReport['confidenceFactors'];
    for (const key of ['agreement', 'quality', 'timeliness', 'specificity', 'independence'] as const) {
        const score = draft.confidenceFactors?.[key];
        if (typeof score !== 'number' || !Number.isFinite(score) || score < 0 || score > 100)
            throw new Error('Each confidence factor must be a number from 0 to 100.');
        factors[key] = Math.round(score);
    }
    const cited = new Set(claims.flatMap(claim => claim.sourceIds));
    const publishers = new Set(sources.filter(source => cited.has(source.id)).map(source => new URL(source.url).hostname.toLowerCase())
        .filter(host => !host.endsWith('vertexaisearch.cloud.google.com') && !host.endsWith('vertexaisearch.cloud.google')));
    const evidenceCoverage = 100 * claims.filter(claim => claim.sourceIds.length).length / claims.length;
    const confidenceCap = publishers.size > 1 ? 100 : publishers.size === 1 ? 60 : 0;
    const dataGaps = [...new Set([...intakeGaps, ...draft.dataGaps.map((gap: unknown) => text(gap, 'Data gap', 1500))])] as string[];
    if (droppedReferences)
        dataGaps.push('Some claim references did not match retrieved sources; unsupported claims remain unverified.');
    if (!publishers.size)
        dataGaps.push('Independent publisher origins could not be established; confidence is withheld.');
    else if (publishers.size === 1)
        dataGaps.push('Cited evidence has only one publisher origin; confidence is capped at 60%.');
    if (!['Low', 'Moderate', 'High', 'Critical', 'Unknown'].includes(draft.impact))
        throw new Error('Provide a valid impact level.');
    const actions: IntelligenceAction[] = draft.actions.map((action: any) => {
        if (!['High', 'Medium', 'Low'].includes(action?.priority))
            throw new Error('Every action needs a priority.');
        return { priority: action.priority, action: text(action.action, 'Action', 1500),
            rationale: text(action.rationale, 'Action rationale', 1500), trigger: text(action.trigger, 'Action trigger', 1000) };
    });
    return {
        reportType: INTELLIGENCE_REPORT_TYPE, organizationId, generatedAt: new Date().toISOString(),
        title: text(draft.title, 'Report title', 200), summary: text(draft.summary, 'Summary'),
        credibility: text(draft.credibility, 'Credibility'), corroboration: text(draft.corroboration, 'Corroboration'),
        implications: text(draft.implications, 'Implications'), impact: draft.impact,
        claims, actions, sources, dataGaps, confidenceFactors: factors,
        verificationScore: Math.round(100 * claims.reduce((total, claim) => total + (claim.status === 'verified' ? 1 : claim.status === 'partial' ? 0.5 : 0), 0) / claims.length),
        confidenceScore: Math.round(Math.min(Object.values(factors).reduce((total, score) => total + score, 0) / 5, evidenceCoverage, confidenceCap)),
        scoreMethod: 'Verification: mean claim support (verified 100, partial 50, disputed or unverified 0). Confidence: mean of five assessed evidence factors, capped by cited claim coverage and publisher diversity (one origin: 60%; none: 0%). Publisher origins are a ceiling, not proof of editorial independence; syndication must be assessed separately.',
        submittedSources, questions: input.questions, context: input.context,
    };
}
